import React, { useEffect, useState } from "react";
import PrivateLayout from "../../layouts/PrivateLayout";
import { fetchSoloDuoRules } from "../../api/api";
import useDecodedToken from "../../utils/useDecodedToken";

function SoloDuoRules() {
  const backgroundImage = "/ww-wallpaper.webp";
  const { token } = useDecodedToken();
  const [rules, setRules] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setIsLoading(true);
    setErrorMessage("");
    fetchSoloDuoRules(token)
      .then((data) => {
        if (cancelled) return;
        setRules(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setErrorMessage(err.message || "Failed to load rules.");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const sectionLabels = {
    description: "Overview",
    scoring: "Scoring",
    pugOnlyRules: "PUG-only rules",
    soloRules: "Solo rules",
    duoRules: "Duo rules",
    notes: "Notes",
  };

  const sectionKeys = [
    "description",
    "scoring",
    "pugOnlyRules",
    "soloRules",
    "duoRules",
    "notes",
  ];

  const hasStructuredRules =
    rules &&
    typeof rules === "object" &&
    !Array.isArray(rules) &&
    sectionKeys.some((key) => rules[key]);

  return (
    <PrivateLayout backgroundImage={backgroundImage}>
      <div className="flex flex-col min-h-[calc(100vh-4rem)] text-white bg-black/40 px-4 py-8">
        <div className="max-w-[1180px] mx-auto w-full">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 drop-shadow-lg">
            {rules?.title || "Solo/Duo Ladder Rules"}
          </h1>
          <p className="text-gray-400 text-sm mb-6">Midnight season 1</p>

          {isLoading && (
            <p className="text-gray-300">Loading rules…</p>
          )}

          {errorMessage && (
            <p className="text-red-400">{errorMessage}</p>
          )}

          {!isLoading && !errorMessage && hasStructuredRules && (
            <div className="space-y-6">
              {sectionKeys.map((key) => {
                const value = rules[key];
                if (value == null || value === "") return null;
                const label = sectionLabels[key] || key;
                return (
                  <section key={key} className="bg-gray-800/60 rounded-lg p-6 border border-gray-700">
                    <h2 className="text-lg font-semibold text-blue-300 mb-3">
                      {label}
                    </h2>
                    <p className="whitespace-pre-wrap text-gray-200 font-sans">
                      {value}
                    </p>
                  </section>
                );
              })}
            </div>
          )}

          {!isLoading && !errorMessage && !hasStructuredRules && rules != null && (
            <div className="prose prose-invert max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-gray-200 bg-gray-800/60 rounded-lg p-6 border border-gray-700">
                {typeof rules === "string"
                  ? rules
                  : rules?.description ?? rules?.text ?? rules?.content ?? JSON.stringify(rules, null, 2)}
              </pre>
            </div>
          )}

          {!isLoading && !errorMessage && !hasStructuredRules && rules == null && (
            <p className="text-gray-400">No rules available.</p>
          )}
        </div>
      </div>
    </PrivateLayout>
  );
}

export default SoloDuoRules;
